import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useBotContext, useBotData } from '../useBotContext';
import BotAHUForm from '../BotAHUForm';
import BotTunnel from '../BotTunnelForm';
import BotAccessoriesPanel from '../BotAccessoriesPanel';
import Loader from '../atom/Loader';
import './styles.scss';

const BotForm: FC = () => {
  const value = useBotData();
  const { value: botData } = useBotContext();
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState<{
    tunnelIndex: number;
    sectionIndex: number;
  } | null>(null);
  const [isAccessoriesOpen, setIsAccessoriesOpen] = useState(false);
  const [accessoriesPosition, setAccessoriesPosition] = useState<{
    top: number;
    height: number;
  } | null>(null);
  const accessoriesPanelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const handleSelectSection = useCallback(
    (tunnelIndex: number, sectionIndex: number) => {
      if (
        selected?.sectionIndex === sectionIndex &&
        selected.tunnelIndex === tunnelIndex
      )
        return;
      setSelected({ tunnelIndex, sectionIndex });
      setIsAccessoriesOpen(false);
    },
    [selected],
  );

  const handleToggleAccessories = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (isAccessoriesOpen) {
        setIsAccessoriesOpen(false);
      } else {
        // Calculate position relative to bot-form__body container
        if (bodyRef.current) {
          const targetRect = event.currentTarget.getBoundingClientRect();
          const bodyRect = bodyRef.current.getBoundingClientRect();
          const relativeTop = targetRect.top - bodyRect.top - 55;
          setAccessoriesPosition({ top: relativeTop, height: bodyRect.height });
        } else {
          setAccessoriesPosition({ top: 0, height: 0 });
        }
        setIsAccessoriesOpen(true);
      }
    },
    [isAccessoriesOpen],
  );

  // Close accessories panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target?.closest('.section-item') &&
        !target?.closest('.bot-accessories')
      ) {
        setSelected(null);
      }
      if (
        accessoriesPanelRef.current &&
        !accessoriesPanelRef.current.contains(event.target as Node) &&
        !target.closest('.accessories-toggle-btn')
      ) {
        setIsAccessoriesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAccessoriesOpen]);

  return (
    <div className="bot-info__content">
      {isLoading && <Loader />}
      <form className="bot-info__form w-100 d-flex h-100 flex-column">
        <BotAHUForm setIsLoading={setIsLoading} />
        <div ref={bodyRef} className="bot-form__body h-100">
          <div className="bot-form__left">
            {value.tunnels.map((tunnel, tIndex) => (
              <BotTunnel
                key={tunnel.id}
                tunnel={tunnel}
                tIndex={tIndex}
                onSelectSection={handleSelectSection}
                onToggleAccessories={handleToggleAccessories}
                selected={
                  selected && selected.tunnelIndex === tIndex
                    ? selected.sectionIndex
                    : null
                }
              />
            ))}
          </div>
          <div className="bot-form__right"></div>
          {isAccessoriesOpen && selected && (
            <BotAccessoriesPanel
              ref={accessoriesPanelRef}
              selected={selected}
              section={
                selected
                  ? (botData.tunnels[selected.tunnelIndex]?.sections[
                      selected.sectionIndex
                    ] ?? null)
                  : null
              }
              tunnelIndex={selected.tunnelIndex}
              sectionIndex={selected.sectionIndex}
              isOpen={isAccessoriesOpen}
              position={accessoriesPosition}
            />
          )}
        </div>
      </form>
    </div>
  );
};

export default BotForm;
